import React, { useCallback, useEffect, useRef, useState } from 'react';
import { hasUserActivated, onUserGesture } from 'helpers/hooks/useUserActivation';
import { mobile } from 'helpers/utils/screensizes';

interface VideoFileProps {
  src: string;
  mobileSrc?: string;
  title: string;
  height: string;
  poster?: string;
}

/**
 * The phone cut, matching `screensizes.mobile` and Tailwind's `sm`. Chosen over the `md` (768px)
 * breakpoint the section lays out against because the mobile video is meant for phones only:
 * every iPhone is at most 440px wide in portrait, while an iPad Mini is already 768px and should
 * be served the desktop file. A phone held in landscape is wider than this and gets the desktop
 * cut too - correct, since that is also when it has the room for it.
 */
export const MOBILE_BREAKPOINT = mobile;

const MIME_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  ogg: 'video/ogg',
  mov: 'video/quicktime',
};

//Omitted rather than guessed when the extension is unfamiliar: a wrong `type` makes the browser
//skip the source outright, which is worse than making it sniff the file.
const mimeTypeOf = (url: string) => {
  const extension = url.split('?')[0].split('.').pop()?.toLowerCase();
  return extension ? MIME_TYPES[extension] : undefined;
};

/**
 * Plays a hosted video file (CDN mp4/webm/...) with sound, once the visitor has interacted with
 * the site. Until then the element sits paused on its poster frame with native controls, so a
 * visitor who landed straight on this page can still start it themselves.
 */
export default function VideoFile({ src, mobileSrc, title, height, poster }: VideoFileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false);

  //One cut for every device unless the CMS supplies a phone-sized one as well. The desktop URL is
  //listed last and carries no media query, so it stays the universal fallback - which also covers
  //the mobile file 404ing, since resource selection moves on to the next <source> on error.
  const sources = mobileSrc
    ? [
        { url: mobileSrc, media: `(max-width: ${MOBILE_BREAKPOINT - 1}px)` },
        { url: src, media: undefined },
      ]
    : [{ url: src, media: undefined }];

  const sourceKey = `${src}|${mobileSrc ?? ''}`;
  const loadedKey = useRef(sourceKey);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || loadedKey.current === sourceKey) return;

    //Swapping <source> children does not restart resource selection on its own.
    loadedKey.current = sourceKey;
    video.load();
  }, [sourceKey]);

  const playWithSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return Promise.resolve(false);

    video.muted = false;
    video.volume = 1;

    return video
      .play()
      .then(() => {
        setIsMuted(false);
        return true;
      })
      .catch(() => false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let soundIsOn = false;

    const attempt = () =>
      playWithSound().then((succeeded) => {
        const video = videoRef.current;
        if (cancelled || !video) return;

        if (succeeded) {
          soundIsOn = true;
          detachGesture();
          return;
        }

        //Reached when the activation was remembered from an earlier page in the session but this
        //document never got one of its own (a hard navigation resets it), so the browser refused
        //the audio. Run muted rather than leaving the video paused, and retry on the next gesture.
        video.muted = true;
        setIsMuted(true);
        video.play().catch(() => undefined);
      });

    //Runs inside the gesture's own handler, which is what Safari wants to allow audible playback.
    const detachGesture = onUserGesture(() => {
      if (!cancelled && !soundIsOn) attempt();
    });

    //A gesture from earlier in the session - most often the click that navigated here - counts too,
    //and it happened before this component existed, so no listener will ever report it.
    if (hasUserActivated()) attempt();

    return () => {
      cancelled = true;
      detachGesture();
    };
  }, [sourceKey, playWithSound]);

  return (
    <div className="relative w-full">
      <video
        ref={videoRef}
        className="w-full bg-black object-contain"
        style={{ height }}
        playsInline
        controls
        preload="metadata"
        poster={poster}
        aria-label={title}
      >
        {sources.map((source) => (
          <source key={source.url} src={source.url} media={source.media} type={mimeTypeOf(source.url)} />
        ))}
      </video>
      {isMuted && (
        <button
          type="button"
          onClick={() => playWithSound()}
          aria-label="Unmute video"
          className="absolute right-16 top-16 rounded-full bg-black/60 px-16 py-8 text-14 font-medium text-white transition-opacity hover:bg-black/80"
        >
          Tap for sound
        </button>
      )}
    </div>
  );
}
